import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useFormik } from 'formik';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { useToggle } from '@cutwise/utils';

import { DriveModel } from '@/dmdrive/drive/entities/DriveModel';
import { UpdateProjectValues } from '@/dmdrive/drive/types/ProjectValues';
import { useStore } from '@/dmdrive/store';
import { ContentModal, InputField } from '@/dmdrive/ui-kit/components';

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: DriveModel;
}

function EditProjectModal(props: EditProjectModalProps) {
  const { isOpen, onClose, project } = props;
  const { isUploadingModel, updateFolder } = useStore('driveStore');

  const [isUpdating, startUpdating, stopUpdating] = useToggle();

  const { values, errors, handleChange, handleSubmit, resetForm } = useFormik<UpdateProjectValues>({
    validateOnMount: false,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      title: '',
    },
    onSubmit: async (values: UpdateProjectValues) => {
      try {
        startUpdating();

        await updateFolder(project.id, values);

        onClose();
      } finally {
        stopUpdating();
      }
    },
    validate(values: UpdateProjectValues) {
      const { title } = values;

      const errors: Partial<Record<keyof UpdateProjectValues, string>> = {};

      if (!title) {
        errors.title = 'Required';
      }

      return errors;
    },
  });

  useEffect(() => {
    if (isOpen) {
      resetForm({
        values: {
          title: project?.title ?? '',
        },
      });
    }
  }, [isOpen]);

  return (
    <ContentModal maxWidth="xs" onClose={onClose} open={isOpen} isLoading={isUploadingModel}>
      <form onSubmit={handleSubmit} onReset={onClose}>
        <ContentModal.Header onClose={onClose}>Update Project</ContentModal.Header>

        <ContentModal.Body>
          <Grid container spacing={2}>
            <Grid size={12}>
              <InputField
                label="Project name"
                name="title"
                value={values.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                isFullWidth
              />
            </Grid>
          </Grid>
        </ContentModal.Body>

        <ContentModal.Footer>
          <Button type="reset">Cancel</Button>

          <LoadingButton loading={isUpdating} type="submit" variant="contained">
            Update
          </LoadingButton>
        </ContentModal.Footer>
      </form>
    </ContentModal>
  );
}

export default observer(EditProjectModal);
